// MB3 auth cookie names, shared by the BFF (server) and the browser packages so
// the two cannot drift. Mirrors mb3-utils `CookieNameForRegion`: the region is
// in the name because NA and EU share a host, and without it the region that
// wrote last owns the other's session.

export type Mb3Environment = 'production' | 'qa' | 'develop' | 'local'

export type Mb3CookiePurpose = 'accessToken' | 'refreshToken' | 'csrfToken'

type BusinessRegion = '' | 'na' | 'eu' | 'local'

export type Mb3CookieNames = {
  // Every name to look for, in order: region-scoped, env-prefixed, legacy.
  read: Record<Mb3CookiePurpose, readonly string[]>
  // The names this environment's session uses. Off production the unprefixed
  // legacy name belongs to production on the shared domain, so it is excluded.
  own: Record<Mb3CookiePurpose, readonly string[]>
}

const ENV_PREFIX: Record<Mb3Environment, '' | 'qa_' | 'dev_' | 'local_'> = {
  production: '',
  qa: 'qa_',
  develop: 'dev_',
  local: 'local_',
}

const PURPOSES: Record<Mb3CookiePurpose, string> = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  csrfToken: 'csrf_token',
}

const REGIONS: readonly BusinessRegion[] = ['na', 'eu', 'local']

// The services' base URL ends in the business region (`…/na`), so it names the
// region without another setting to keep in step.
export function businessRegionFromBaseUrl(
  baseUrl: string | undefined,
): BusinessRegion {
  if (!baseUrl) {
    return ''
  }

  let pathname: string

  try {
    pathname = new URL(baseUrl).pathname
  } catch {
    return ''
  }

  const segments = pathname.split('/').filter(Boolean)
  const region = segments.length === 1 ? segments[0]!.toLowerCase() : ''

  return (REGIONS as readonly string[]).includes(region)
    ? (region as BusinessRegion)
    : ''
}

export function buildMb3CookieNames(
  env: Mb3Environment,
  region: string,
): Mb3CookieNames {
  const prefix = ENV_PREFIX[env] ?? ''
  const normalizedRegion = region.trim().toLowerCase()

  const namesFor = (purpose: Mb3CookiePurpose, includeLegacy: boolean) => {
    const suffix = PURPOSES[purpose]
    const names = [
      normalizedRegion && `mb3_${prefix}${normalizedRegion}_${suffix}`,
      prefix && `mb3_${prefix}${suffix}`,
      includeLegacy && `mb3_${suffix}`,
    ]

    return names.filter((name): name is string => Boolean(name))
  }

  const build = (includeLegacy: boolean) => ({
    accessToken: namesFor('accessToken', includeLegacy),
    refreshToken: namesFor('refreshToken', includeLegacy),
    csrfToken: namesFor('csrfToken', includeLegacy),
  })

  return {
    read: build(true),
    // Production's legacy name is its own; elsewhere it is production's.
    own: build(!prefix),
  }
}

// This app's names, from the two settings the BFF reads too. next.config.ts
// inlines both into the browser bundle.
export function appMb3CookieNames(): Mb3CookieNames {
  return buildMb3CookieNames(
    (process.env.NEXT_PUBLIC_MB3_ENV || 'production') as Mb3Environment,
    businessRegionFromBaseUrl(process.env.NEXT_MB3_BASE_URL),
  )
}
